import { CategoryAreaPinComponentSettings, toJSONContent } from '@lessonup/pins-shared';
import { color, cx, spacing, styled, useTranslation } from '@lessonup/ui-components';
import { Editor } from '@tiptap/core';
import React, { useMemo } from 'react';
import { TiptapViewer } from '../../components/Tiptap/TipTapViewer';
import { TipTapComponent } from '../../components/Tiptap/Tiptap.utils';
import { colorSets } from '../../foundations/colorSets/colorSets';
import { computeCategoryStyles } from './Category.utils';
import { useCategoryHandlers } from './hooks/useCategoryHandlers';

export type CategorySettings = CategoryAreaPinComponentSettings['categories'][number];

export interface CategoryProps extends Pick<CategoryAreaPinComponentSettings, 'layoutMode'> {
  category: CategorySettings;
  active?: boolean;
  editable?: boolean;
  isCategoryAreaActive?: boolean;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseMove?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
  pinId?: string;
  isTextEditMode?: boolean;
  Renderer?: TipTapComponent;
  registerEditor?: (editor: Editor) => void;
  componentId?: string;
  setModeTextEdit?: (componentId: string, categoryId: string) => void;
}

const Category: React.FC<CategoryProps> = ({
  category,
  active = false,
  editable = false,
  isCategoryAreaActive = false,
  layoutMode,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onContextMenu,
  children,
  pinId,
  isTextEditMode = false,
  Renderer,
  registerEditor,
  componentId,
  setModeTextEdit,
}) => {
  const { t } = useTranslation();

  // Use individual handlers when active (for text edit mode)
  const categoryHandlers = useCategoryHandlers({
    componentId: componentId || '',
    categoryId: category.id,
    active,
    editable,
    isTextEditMode,
    setModeTextEdit,
    onMouseDown,
    onMouseUp,
    layoutMode,
  });

  const tiptapContent = useMemo(() => {
    return typeof category.text === 'string' ? toJSONContent(category.text) : category.text;
  }, [category.text]);

  const renderCategoryContent = () => {
    if (active && isTextEditMode && pinId && Renderer) {
      return (
        <Renderer
          pinId={pinId}
          pinComponentId={category.id}
          active={active}
          isTextEditMode={true}
          editable={editable}
          fragmentKey={`${category.id}-text`}
          placeholder={t('text.placeholder.default')}
          registerEditor={categoryHandlers.registerEditor}
        />
      );
    }

    return (
      <>
        {Renderer && pinId ? (
          <Renderer
            pinId={pinId}
            pinComponentId={category.id}
            fragmentKey={`${category.id}-text`}
            editable={editable}
            active={active}
            isTextEditMode={false}
            registerEditor={active ? categoryHandlers.registerEditor : registerEditor}
          />
        ) : (
          <TiptapViewer content={tiptapContent} />
        )}
      </>
    );
  };

  const categoryStyles = useMemo(
    () =>
      computeCategoryStyles({
        categoryStyle: category.style,
        active,
        placeholderText: t('text.placeholder.default'),
      }),
    [category.style, active, t]
  );

  return (
    <StyledCategory
      ref={categoryHandlers.categoryRef}
      style={categoryStyles}
      onMouseDown={active ? categoryHandlers.handleMouseDown : onMouseDown}
      onMouseMove={active ? categoryHandlers.handleMouseMove : onMouseMove}
      onMouseUp={active ? categoryHandlers.handleMouseUp : onMouseUp}
      onContextMenu={onContextMenu}
      className={cx(
        active ? 'active' : undefined,
        isTextEditMode ? 'edit' : undefined,
        `layout-${layoutMode.toLowerCase()}`
      )}
      data-editable={editable}
      data-category-area-active={isCategoryAreaActive}
    >
      {renderCategoryContent()}
      {children}
    </StyledCategory>
  );
};

const StyledCategory = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  cursor: pointer;
  user-select: none;

  background-color: var(--category-background-color);
  border: var(--category-border-width) var(--category-border-style) var(--category-border-color);
  border-radius: var(--category-border-radius);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: ${spacing.size4};
  box-sizing: border-box;
  overflow: hidden;

  background-image: var(--category-background-image, none);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &[data-editable='true'][data-category-area-active='true']:not(.active):hover {
    outline: 1px solid ${colorSets.disabled.strokeSubjacent};
  }

  &[data-editable='true']:hover {
    border-color: ${colorSets.active.on};
  }

  &.active.layout-auto {
    outline: 2px solid ${colorSets.active.on};
  }

  &.active.layout-custom::before {
    content: ' ';
    position: absolute;
    top: -10px;
    left: -10px;
    width: calc(100% + 20px);
    height: calc(100% + 20px);
    z-index: -1;
  }

  &.edit {
    outline: 2px solid ${colorSets.active.on};
    cursor: text;

    *:focus .selected-text {
      background-color: initial;
    }
    .selected-text {
      background-color: ${color.additional.disabled.background};
    }
  }

  &:not(.edit) {
    cursor: default;
    caret-color: transparent;

    span::selection {
      background-color: transparent;
      color: inherit;
    }

    .ProseMirror {
      pointer-events: none;
    }
  }

  &.edit .ProseMirror {
    pointer-events: auto;
  }

  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  text-align: center;
  overflow: visible;
  white-space: normal;
  min-height: 100%;

  .tiptap-root {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }

  .ProseMirror:focus {
    outline: none;
  }

  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: var(--category-placeholder-text);
    color: ${color.neutral.surface.text};
    opacity: 0.6;
    font-style: italic;
    pointer-events: none;
  }
`;

export default Category;
